import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicMarketingLayout from '../components/layout/PublicMarketingLayout';
import MelioBrandMark from '../components/brand/MelioBrandMark';
import OliveBranch from '../components/marketing/OliveBranch';
import { clearEnrollmentDraft } from '../services/enrollmentDraftService';

interface NextStep {
  numeral: string;
  body: string;
}

const MEMBERSHIP_STEPS: NextStep[] = [
  { numeral: 'I', body: 'We confirm your membership activation, first payment, and year-long continuity setup.' },
  { numeral: 'II', body: 'We confirm your quarterly review rhythm and what inputs or tests are due next.' },
  { numeral: 'III', body: 'You can upload new health data and keep your record current through the year.' },
  { numeral: 'IV', body: 'Add-ons such as biological age testing can be arranged separately where relevant.' },
];

const ASSESSMENT_STEPS: NextStep[] = [
  { numeral: 'I', body: 'We confirm consent and send your genetics kit guidance.' },
  { numeral: 'II', body: 'We send baseline bloods instructions, including Lp(a) and core biomarkers.' },
  { numeral: 'III', body: 'You book your clinical history call from your dashboard — a doctor will take your history.' },
  { numeral: 'IV', body: 'You receive a clinician-reviewed surveillance plan with your next due tests.' },
];

const BookThankYouPage: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const paid = (location.state as { paid?: boolean })?.paid;
  const searchParams = new URLSearchParams(location.search);
  const fromStripe = searchParams.get('paid') === '1' || paid;
  const paymentId = searchParams.get('paymentId');
  const packageId = searchParams.get('packageId');
  const refNum = paymentId ? `MEL-${paymentId.slice(0, 8).toUpperCase()}` : null;
  // `plus_membership` is retired, but historical confirmation links may still
  // carry it, so both ids resolve to the membership journey.
  const isMembershipPackage =
    packageId === 'core_membership' || packageId === 'plus_membership';
  const steps = isMembershipPackage ? MEMBERSHIP_STEPS : ASSESSMENT_STEPS;

  useEffect(() => {
    if (fromStripe) clearEnrollmentDraft();
  }, [fromStripe]);

  return (
    <PublicMarketingLayout>
      <main className="mx-auto max-w-3xl px-6 py-20 md:px-10 md:py-28">
        {/* Ceremonial seal */}
        <div className="flex justify-center">
          <MelioBrandMark variant="icon" height={72} />
        </div>

        {/* Heading */}
        <div className="mt-10 text-center">
          <p className="gh-eyebrow mb-5">
            {fromStripe ? 'Payment received' : 'Your enquiry is with us'}
          </p>
          <h1
            className="gh-serif"
            style={{
              fontSize: 'clamp(2.25rem, 4.5vw, 3.25rem)',
              lineHeight: 1.1,
              margin: 0,
              color: 'var(--gh-graphite)',
            }}
          >
            <em style={{ fontStyle: 'italic' }}>Welcome</em> to the practice.
          </h1>
          <p
            className="gh-serif mx-auto mt-6 max-w-lg"
            style={{
              fontStyle: 'italic',
              color: 'var(--gh-ash)',
              fontSize: '1.125rem',
              lineHeight: 1.55,
            }}
          >
            A clinician will be in touch by email shortly. In the meantime, a brief
            account of what happens next.
          </p>
          {refNum && (
            <p
              className="mt-4"
              style={{
                fontSize: '0.6875rem',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--gh-ash)',
              }}
            >
              Reference · {refNum}
            </p>
          )}

          <div className="mt-10 flex justify-center">
            <OliveBranch width={96} color="var(--gh-oxblood)" />
          </div>
        </div>

        {/* Next steps */}
        <section
          className="mx-auto mt-14 max-w-2xl"
          style={{ borderTop: '1px solid var(--gh-rule)' }}
        >
          <p className="gh-eyebrow-ash mt-12 mb-8 text-center">Next steps</p>
          {steps.map((step, index) => (
            <div key={step.numeral}>
              <div
                className="grid items-start gap-x-8 py-7"
                style={{ gridTemplateColumns: '56px 1fr' }}
              >
                <div
                  className="gh-serif"
                  style={{
                    color: 'var(--gh-oxblood)',
                    fontWeight: 300,
                    fontStyle: 'italic',
                    fontSize: '1.75rem',
                    lineHeight: 1,
                    letterSpacing: '0.02em',
                  }}
                >
                  {step.numeral}.
                </div>
                <p
                  className="text-base leading-relaxed md:text-lg"
                  style={{ color: 'var(--gh-graphite)', maxWidth: '56ch', lineHeight: 1.6 }}
                >
                  {step.body}
                </p>
              </div>
              {index < steps.length - 1 && <hr className="gh-rule" />}
            </div>
          ))}
        </section>

        {/* CTAs */}
        <div className="mt-14 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          {fromStripe && (
            <>
              <Link
                to="/book/clinical-history"
                className="gh-btn-primary"
                style={{ minWidth: 260 }}
              >
                Book your clinical history
              </Link>
              <Link
                to={currentUser ? '/home' : '/login'}
                className="gh-btn-secondary"
                style={{ minWidth: 240 }}
              >
                {currentUser ? 'Go to your home' : 'Log in to continue'}
              </Link>
            </>
          )}
          {!fromStripe && (
            <Link to="/" className="gh-btn-secondary" style={{ minWidth: 200 }}>
              Back to home
            </Link>
          )}
        </div>

        {/* Closing flourish */}
        <div className="mt-16 flex justify-center">
          <OliveBranch width={56} color="var(--gh-oxblood)" />
        </div>
        <p
          className="gh-serif mt-6 text-center"
          style={{
            fontStyle: 'italic',
            color: 'var(--gh-ash)',
            fontSize: '0.9375rem',
            lineHeight: 1.55,
          }}
        >
          With care — from the practice.
        </p>
      </main>
    </PublicMarketingLayout>
  );
};

export default BookThankYouPage;
